import request from "superagent";
import { getAuthHeader } from "src/utils/superagent";
import {
  FacilityCalendarCounters,
  FacilityNegotiationCounters,
  FacilityShift,
  FacilityShiftCounters,
  IFetchFacilityCalendarData,
  FetchFacilityCalendarAvailableWorkerCountData,
  FetchFacilityCalendarAvailableWorkersData,
  FetchFacilityCalendarAvailableWorkersResponse,
} from "./types";
import { logError } from "src/appV2/lib/analytics";
import { environmentConfig } from "@src/appV2/environment";
import { isDefined } from "@clipboard-health/util-ts";

const workerServiceUrl = environmentConfig.REACT_APP_WORKER_SERVICE_URL;

const defaultShiftTimes = {
  am: {
    start: {
      hour: 5,
      minute: 0,
    },
    duration: {
      hours: 8,
      minutes: 0,
    },
  },

  pm: {
    start: {
      hour: 5,
      minute: 0,
    },
    duration: {
      hours: 8,
      minutes: 0,
    },
  },

  noc: {
    start: {
      hour: 5,
      minute: 0,
    },
    duration: {
      hours: 8,
      minutes: 0,
    },
  },
  custom: {
    start: {
      hour: 7,
      minute: 0,
    },
    duration: {
      hours: 8,
      minutes: 0,
    },
  },
  surg: {
    start: {
      hour: 9,
      minute: 0,
    },
    duration: {
      hours: 4,
      minutes: 0,
    },
  },
};
export type DefaultShiftTimes = typeof defaultShiftTimes;

const fetchFacilityCalendarData = async (
  query: IFetchFacilityCalendarData
): Promise<FacilityCalendarCounters> => {
  const [shiftsData, negotiationsData] = await Promise.all([
    fetchFacilityCalendarShifts(query),
    fetchFacilityCalendarNegotiations({
      ...query,
      agentReq: query.type,
    }),
  ]);
  // Merging negotiationsData to shiftsData, since if some date has negotiation,
  // it will definitely have a shift (but not vice-versa).
  Object.keys(shiftsData).forEach((dateAsString) => {
    if (!negotiationsData[dateAsString]) {
      return;
    }
    shiftsData[dateAsString] = Object.assign(
      shiftsData[dateAsString],
      negotiationsData[dateAsString]
    );
  });
  return shiftsData;
};

const fetchFacilityCalendarShifts = async (
  query: IFetchFacilityCalendarData
): Promise<FacilityShiftCounters> => {
  const { body } = await request
    .get(`${global.api}/calendar/facilityCount`)
    .set(await getAuthHeader())
    .query(query);
  return body;
};

const fetchFacilityCalendarNegotiations = async ({
  agentReq,
  dateFilter,
  tmz,
  facilityId,
}: {
  agentReq: string | undefined;
  dateFilter: {
    start: string;
    end: string;
  };
  tmz: string;
  facilityId: string;
}): Promise<FacilityNegotiationCounters> => {
  const { body } = await request
    .get(`${global.api}/negotiation/facilityOpenNegotiationCount`)
    .set(await getAuthHeader())
    .query({
      agentReq,
      dateFilter,
      tmz,
      facilityId,
    });
  return body;
};

const fetchFacilityDayShifts = async (query: {
  admin: boolean;
  dateFilter: {
    start: string;
    end: string;
  };
  tmz: string;
  type: string | undefined;
  facilityId: string;
  includeNegotiations: boolean;
}): Promise<FacilityShift[]> => {
  const { body } = await request
    .get(`${global.api}/calendar/facility`)
    .set(await getAuthHeader())
    .query(query);

  return body;
};

const fetchShiftTimes = async ({
  userId,
  qualification,
}: {
  userId: string;
  qualification?: string;
}): Promise<DefaultShiftTimes> => {
  const { body } = await request
    .get(`${global.api}/shiftTimes/get/${userId}`)
    .set(await getAuthHeader())
    .query(isDefined(qualification) ? { qualification } : {});

  let qualificationSpecificEntries = {};
  if (
    isDefined(qualification) &&
    isDefined(body.qualificationSpecific) &&
    isDefined(body.qualificationSpecific[qualification])
  ) {
    qualificationSpecificEntries = body.qualificationSpecific[qualification];
  }

  return {
    ...defaultShiftTimes,
    ...body.standardDefs,
    ...qualificationSpecificEntries,
  };
};

const updateDefaultShiftTime = async (requestData: {
  data: {
    start: {
      hour: number;
      minute: number;
    };
    duration: {
      hours: number;
      minutes: number;
    };
  };
  facilityId: string;
  name: keyof DefaultShiftTimes;
  qualification?: string;
}) => {
  const { body } = await request
    .post(`${global.api}/shiftTimes/update`)
    .set(await getAuthHeader())
    .send(requestData);

  let qualificationSpecificEntries = {};
  if (
    isDefined(requestData.qualification) &&
    isDefined(body.qualificationSpecific) &&
    isDefined(body.qualificationSpecific[requestData.qualification])
  ) {
    qualificationSpecificEntries = body.qualificationSpecific[requestData.qualification];
  }

  return {
    ...body.standardDefs,
    ...qualificationSpecificEntries,
  };
};

const confirmShift = async (data) => {
  return request
    .post(`${global.api}/shift/hcfConfirm`)
    .set(await getAuthHeader())
    .send(data)
    .then(({ body }) => body);
};

export type PayTable = {
  floor: number;
  marketBase: number;
  marketFill: number;
  sd: number;
  weekdayBase: number;
  weekendBase: number;
};

export enum RecommendedChargeReason {
  CHARGE_BLOCKED_FACILITY = "CHARGE_BLOCKED_FACILITY",
}

export type ChargeOption = {
  standard: number;
  recommended?: number;
  reason?: RecommendedChargeReason;
  fill?: number;
};

export interface EstimatedCharge {
  charge?: ChargeOption;
  nurseCharges?: { LVN: ChargeOption; RN: ChargeOption };
}
const fetchEstimatedCharge = async (
  workplaceId: string,
  agentReq: string,
  shiftType: string,
  shiftStartDate: string
): Promise<EstimatedCharge> => {
  return request
    .get(`${global.api}/v2/shifts/estimated-charge`)
    .set(await getAuthHeader())
    .query({ workplaceId, agentReq, shiftType, shiftStartDate })
    .then(({ body }) => body);
};

const fetchFacilityCalendarAvailableWorkersCount = async (
  query: FetchFacilityCalendarAvailableWorkerCountData,
  workplaceId: string
) => {
  return request
    .get(`${workerServiceUrl}/v1/workplaces/${workplaceId}/calendar/available-worker-count`)
    .set(await getAuthHeader())
    .query(query)
    .then(({ body }) => body)
    .catch((error) =>
      logError("Error in fetchFacilityCalendarAvailableWorkersCount", {
        error,
      })
    );
};

const fetchFacilityCalendarAvailableWorkers = async (
  query: FetchFacilityCalendarAvailableWorkersData,
  workplaceId: string
): Promise<FetchFacilityCalendarAvailableWorkersResponse> => {
  return request
    .get(`${workerServiceUrl}/v1/workplaces/${workplaceId}/calendar/available-workers`)
    .set(await getAuthHeader())
    .query(query)
    .then(({ body }) => body)
    .catch((error) =>
      logError("Error in fetchFacilityCalendarAvailableWorkers", {
        error,
      })
    );
};

export async function deleteShiftInvite(shiftInviteId: string): Promise<void> {
  await request.delete(`${global.api}/shift-invites/${shiftInviteId}`).set(await getAuthHeader());
}

export {
  fetchFacilityCalendarData,
  fetchFacilityDayShifts,
  fetchShiftTimes,
  defaultShiftTimes,
  updateDefaultShiftTime,
  confirmShift,
  fetchEstimatedCharge,
  fetchFacilityCalendarAvailableWorkersCount,
  fetchFacilityCalendarAvailableWorkers,
};
